import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/* ─── About Hero Background ─── */
const ABOUT_HERO_BG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20golden%20light%20rays%20emanating%20from%20center%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20soft%20glowing%20particles%20luxurious%20atmosphere%20no%20text&width=1920&height=800&seq=abouthero1&orientation=landscape';
import { useSEO, generateOrganizationSchema, generateWebPageSchema, generateBreadcrumbSchema } from '@/utils/seo';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import ElegantHeader from '@/components/feature/ElegantHeader';
import ElegantFooter from '@/components/feature/ElegantFooter';
import ElegantHero from '@/components/feature/ElegantHero';
import ElegantSectionTitle from '@/components/feature/ElegantSectionTitle';

/* ─── Value Card ─── */
interface ValueCardProps {
  title: string;
  description: string;
  index: number;
  visible: boolean;
}

function ValueCard({ title, description, index, visible }: ValueCardProps) {
  return (
    <div
      className="group p-6 md:p-8 border transition-all duration-500 cursor-default"
      style={{
        borderColor: 'rgba(212, 207, 196, 0.5)',
        background: '#FFFFFF',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s ease ${0.08 + index * 0.07}s, transform 0.7s ease ${0.08 + index * 0.07}s, border-color 0.3s`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 162, 39, 0.4)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 207, 196, 0.5)';
      }}
    >
      <div
        className="w-8 h-8 flex items-center justify-center mb-4"
        style={{
          border: '1px solid rgba(201, 162, 39, 0.3)',
        }}
      >
        <i className="ri-check-line text-sm" style={{ color: '#C9A227' }} />
      </div>
      <h3
        className="text-xl font-medium mb-4"
        style={{
          fontFamily: "'Noto Serif JP', serif",
          color: '#1A1A1A',
          letterSpacing: '0.02em',
        }}
      >
        {title}
      </h3>
      <p
        className="text-sm leading-loose"
        style={{
          color: '#3E3B34',
          fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
        }}
      >
        {description}
      </p>
    </div>
  );
}

/* ─── Info Row ─── */
interface InfoRowProps {
  label: string;
  value: string | React.ReactNode;
  isLast?: boolean;
}

function InfoRow({ label, value, isLast = false }: InfoRowProps) {
  return (
    <div
      className={`flex flex-col sm:flex-row py-4 px-6 gap-2 sm:gap-8 ${!isLast ? 'border-b' : ''} transition-colors duration-300 hover:bg-warmgray-50`}
      style={{ borderColor: 'rgba(212, 207, 196, 0.4)' }}
    >
      <div
        className="text-xs tracking-widest-plus uppercase sm:w-40 flex-shrink-0 pt-0.5"
        style={{
          color: '#3E3B34',
          fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        className="text-base leading-loose"
        style={{
          color: '#3E3B34',
          fontFamily: "'Noto Serif JP', serif",
          lineHeight: '1.8',
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ─── Award Card ─── */
interface AwardCardProps {
  text: string;
  index: number;
  visible: boolean;
}

function AwardCard({ text, index, visible }: AwardCardProps) {
  return (
    <div
      className="group flex items-start gap-4 p-5 border transition-all duration-300"
      style={{
        borderColor: 'rgba(212, 207, 196, 0.4)',
        background: '#FFFFFF',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ease ${0.04 + index * 0.05}s, transform 0.6s ease ${0.04 + index * 0.05}s, border-color 0.3s`,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201, 162, 39, 0.3)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(212, 207, 196, 0.4)';
      }}
    >
      <div
        className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5"
        style={{
          border: '1px solid rgba(201, 162, 39, 0.25)',
        }}
      >
        <i className="ri-award-line text-xs" style={{ color: '#C9A227' }} />
      </div>
      <p
        className="text-base"
        style={{
          color: '#3E3B34',
          fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
          lineHeight: '1.8',
        }}
      >
        {text}
      </p>
    </div>
  );
}

/* ─── Main Page ─── */
export default function AboutPage() {
  const { t, i18n } = useTranslation();

  const getSchemaLang = () => {
    const map: Record<string, string> = { ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', 'zh-TW': 'zh-TW' };
    return map[i18n.language] || 'ja-JP';
  };

  /* Scroll reveal hooks */
  const { ref: missionRef, visible: missionVisible } = useScrollReveal({ threshold: 0.15 });
  const { ref: valuesRef, visible: valuesVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: companyRef, visible: companyVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: businessRef, visible: businessVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: affiliationsRef, visible: affiliationsVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: clientsRef, visible: clientsVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: partnersRef, visible: partnersVisible } = useScrollReveal({ threshold: 0.2 });
  const { ref: awardsRef, visible: awardsVisible } = useScrollReveal({ threshold: 0.08 });

  useSEO({
    title: t('seo.aboutTitle'),
    description: t('seo.aboutDescription'),
    keywords: t('seo.aboutKeywords'),
    path: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateWebPageSchema(t('seo.aboutTitle'), t('seo.aboutDescription'), '/about', getSchemaLang()),
        generateBreadcrumbSchema([
          { name: t('nav.topPage'), path: '/' },
          { name: t('nav.about'), path: '/about' },
        ]),
      ],
    },
  });

  const values = [
    { titleKey: 'values.clientFirst', descKey: 'values.clientFirstDesc' },
    { titleKey: 'values.dataDriven', descKey: 'values.dataDrivenDesc' },
    { titleKey: 'values.innovation', descKey: 'values.innovationDesc' },
    { titleKey: 'values.integrity', descKey: 'values.integrityDesc' },
    { titleKey: 'values.collaboration', descKey: 'values.collaborationDesc' },
    { titleKey: 'values.improvement', descKey: 'values.improvementDesc' },
  ];

  const affiliations = ['affiliation1', 'affiliation2', 'affiliation4', 'affiliation5'] as const;

  return (
    <div className="min-h-screen" style={{ background: '#FAF9F6' }}>
      <ElegantHeader />

      {/* Hero */}
      <ElegantHero
        title={t('about.title')}
        subtitle={t('home.aboutSubtitle')}
        tag="About Us"
        backgroundImage={ABOUT_HERO_BG}
        breadcrumb={[
          { label: t('nav.topPage'), to: '/' },
          { label: t('nav.about') },
        ]}
      />

      {/* Main Content */}
      <main className="py-20 md:py-28 px-6 md:px-10 max-w-[1100px] mx-auto space-y-24 md:space-y-32">
        {/* Mission */}
        <section ref={missionRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Mission"
            title={t('about.missionTitle')}
          />
          <div className="mt-10 space-y-5 pl-6 border-l" style={{ borderColor: 'rgba(201, 162, 39, 0.3)' }}>
            <p
              className="text-base leading-relaxed"
              style={{
                color: '#3E3B34',
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                opacity: missionVisible ? 1 : 0,
                transform: missionVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
              }}
            >
              {t('about.missionText1')}
            </p>
            <p
              className="text-base leading-relaxed"
              style={{
                color: '#3E3B34',
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                opacity: missionVisible ? 1 : 0,
                transform: missionVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s',
              }}
            >
              {t('about.missionText2')}
            </p>
          </div>
        </section>

        {/* Values */}
        <section ref={valuesRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Values"
            title={t('about.valuesTitle')}
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <ValueCard
                key={i}
                title={t(v.titleKey)}
                description={t(v.descKey)}
                index={i}
                visible={valuesVisible}
              />
            ))}
          </div>
        </section>

        {/* Company Info */}
        <section ref={companyRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Company"
            title={t('about.companyInfo')}
          />
          <div
            className="mt-10 border overflow-hidden"
            style={{
              borderColor: 'rgba(212, 207, 196, 0.5)',
              background: '#FFFFFF',
              opacity: companyVisible ? 1 : 0,
              transform: companyVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <InfoRow
              label={t('about.companyName')}
              value={`${t('about.companyNameValue')} / ${t('about.companyNameEn')}`}
            />
            <InfoRow
              label={t('about.established')}
              value={t('about.establishedValue')}
            />
            <InfoRow
              label={t('about.capital')}
              value={t('about.capitalValue')}
            />
            <InfoRow
              label={t('about.ceo')}
              value={t('about.ceoValue')}
            />
            <InfoRow
              label={t('about.headquarters')}
              value={
                <>
                  {t('about.headquartersPostal')} {t('about.headquartersAddress1')}<br />
                  {t('about.headquartersAddress2')}
                </>
              }
            />
            <InfoRow
              label={t('about.contact')}
              value={
                <a
                  href="mailto:info@tradfit.com"
                  className="no-underline transition-colors duration-300"
                  style={{ color: '#C9A227' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
                  }}
                >
                  info@tradfit.com
                </a>
              }
              isLast
            />
          </div>
        </section>

        {/* Business Content */}
        <section ref={businessRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Business"
            title={t('about.businessContent')}
          />
          <div
            className="mt-10 border overflow-hidden"
            style={{
              borderColor: 'rgba(212, 207, 196, 0.5)',
              background: '#FFFFFF',
              opacity: businessVisible ? 1 : 0,
              transform: businessVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            {(t('about.businessItems', { returnObjects: true }) as string[]).map((item, i, arr) => (
              <div
                key={i}
                className={`flex items-start gap-5 px-6 md:px-8 py-5 transition-colors duration-300 hover:bg-amber-50/30 ${i < arr.length - 1 ? 'border-b' : ''}`}
                style={{ borderColor: 'rgba(212, 207, 196, 0.35)' }}
              >
                <div
                  className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    border: '1px solid rgba(201, 162, 39, 0.3)',
                    background: 'rgba(201, 162, 39, 0.04)',
                  }}
                >
                  <span
                    className="text-xs font-medium"
                    style={{ color: '#C9A227', fontFamily: "'Inter', sans-serif" }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <p
                  className="text-base pt-1"
                  style={{
                    color: '#3E3B34',
                    fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                    lineHeight: '1.8',
                  }}
                >
                  {item}
                </p>
              </div>
            ))}

          </div>
        </section>



        {/* Major Clients */}
        <section ref={clientsRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Clients"
            title={t('about.majorClients')}
          />
          <div
            className="mt-10 p-6 md:p-8 border"
            style={{
              borderColor: 'rgba(212, 207, 196, 0.5)',
              background: '#FFFFFF',
              opacity: clientsVisible ? 1 : 0,
              transform: clientsVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <p
              className="text-base"
              style={{
                color: '#3E3B34',
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                lineHeight: '1.8',
              }}
            >
              {t('about.majorClientsText')}
            </p>
          </div>
        </section>

        {/* Partners */}
        <section ref={partnersRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Partners"
            title={t('about.partners')}
          />
          <div
            className="mt-10 p-6 md:p-8 border"
            style={{
              borderColor: 'rgba(212, 207, 196, 0.5)',
              background: '#FFFFFF',
              opacity: partnersVisible ? 1 : 0,
              transform: partnersVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <p
              className="text-base"
              style={{
                color: '#3E3B34',
                fontFamily: "'Noto Sans JP', 'Hiragino Kaku Gothic ProN', 'Meiryo', sans-serif",
                lineHeight: '1.8',
              }}
            >
              {t('about.partnersText')}
            </p>
          </div>
        </section>

        {/* Awards */}
        <section ref={awardsRef as React.RefObject<HTMLElement>}>
          <ElegantSectionTitle
            tag="Awards"
            title={t('about.awards')}
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[3, 1, 5, 9, 10, 6].map((n) => (
              <AwardCard
                key={n}
                text={t(`about.award${n}`)}
                index={n - 1}
                visible={awardsVisible}
              />
            ))}
          </div>
        </section>
      </main>

      <ElegantFooter />
    </div>
  );
}